import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../custom/customDropdown';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import { useSelector } from 'react-redux';
import KazneKazne from './KazneKazne';
import AktivnostiDijagram from './AktivnostiDijagram';
import VozilaDijagram from './VozilaDijagram';
import AnalizaNadoknada from './AnalizaNadoknada';
import { propisiZaDrzavu } from './propisi';
import { segmentiULokalno, poljeULokalno, opsegZaUpit, uOpsegu,
         fmtDatum, fmtDatumVreme } from './vreme';
import './Kazne.css';

/* Izveštaji su podeljeni na dve grupe: podaci sa kartica VOZAČA i podaci
   sa jedinica VOZILA (VU). Svaka grupa ima svoje tabove. */
const TABS_VOZACI = [
  { index: 0, kljuc: 'taho.pregledAktivnosti' },
  { index: 4, kljuc: 'taho.dijagramAktivnosti' },
  { index: 1, kljuc: 'taho.lokacijeDrzave' },
  { index: 2, kljuc: 'taho.vozilaTab' },
  { index: 3, kljuc: 'taho.kazneAetr' },
  { index: 5, kljuc: 'taho.poslednjaCitanja' },
  { index: 7, kljuc: 'taho.fajloviCitanja' },
  { index: 6, kljuc: 'taho.analizaNadoknada' },
];

const TABS_VOZILA = [
  { index: 10, kljuc: 'taho.pregledAktivnosti' },
  { index: 11, kljuc: 'taho.dijagramAktivnosti' },
  { index: 12, kljuc: 'taho.poslednjaCitanja' },
  { index: 13, kljuc: 'taho.fajloviCitanja' },
];

/* Tabovi bez filtera po datumu/vozaču (sami povlače svoje podatke).
   KAZNE AETR (3) koristi ZAJEDNIČKI filter, pa nije u ovoj listi. */
const TABS_BEZ_FILTERA = [5, 6, 7, 12, 13];

/* Link do sačuvanog fajla — ista konvencija kao u Administraciji (unos vozila):
   u bazi stoji relativna putanja, a servira se preko /API/files. */
const getDocumentLocation = (putanja) => {
  if (!putanja) return null;
  const normalized = String(putanja).replace(/\\/g, '/').replace(/^C:\/Dokumenta\//, '');
  return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(normalized.replace(/^\//, ''))}`;
};

/* Zakonski rokovi za očitavanje: kartica vozača 28 dana, jedinica vozila (VU) 90 dana. */
const ROK_KARTICA_DANA = 28;
const ROK_VOZILO_DANA  = 90;

const ACTIVITY_LABELS = {
  'break/rest':   'Odmor/Pauza',
  availability:   'Raspoloživost',
  work:           'Rad',
  driving:        'Vožnja',
  unknown:        'Nepoznato',
  shortrest:      'Kratki odmor',
};

function minutesToHHMM(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/* Tahograf čuva UTC. Ranije je ovde stajalo `d.getDate()` / `d.getHours()` —
   zona pregledača — pa je isti izveštaj izgledao drugačije sa računara u drugoj
   zoni. Sada ide kroz `vreme.js`, istu zonu koju koristi i dijagram. */
function formatDT(val) {
  if (!val) return '—';
  const imaVreme = /d{2}:d{2}/.test(String(val));
  const ms = Date.parse(val);
  if (!Number.isFinite(ms)) return '—';
  return imaVreme ? fmtDatumVreme(ms) : fmtDatum(ms);
}

const ACT_COLS = [
  { field: 'HolderName',   header: 'Vozač' },
  { field: 'CardNumber',   header: 'Kartica' },
  { field: 'ActivityDate', header: 'Datum' },
  { field: 'ActivityType', header: 'Aktivnost' },
  { field: 'StartMinute',  header: 'Početak' },
  { field: 'Duration',     header: 'Trajanje' },
];

const PLC_COLS = [
  { field: 'HolderName',  header: 'Vozač' },
  { field: 'CardNumber',  header: 'Kartica' },
  { field: 'EntryTime',   header: 'Vreme ulaska' },
  { field: 'Drzava',      header: 'Država' },
  { field: 'OdometerKm',  header: 'Km (odometar)' },
];

const VHL_COLS = [
  { field: 'HolderName',                header: 'Vozač' },
  { field: 'CardNumber',                header: 'Kartica' },
  { field: 'VehicleRegistrationNumber', header: 'Registracija' },
  { field: 'VehicleRegistrationNation', header: 'Država reg.' },
  { field: 'UseBegin',                  header: 'Korišćenje od' },
  { field: 'UseEnd',                    header: 'Korišćenje do' },
  { field: 'OdometerBeginKm',           header: 'Km početak' },
  { field: 'OdometerEndKm',             header: 'Km kraj' },
  { field: 'Tudje',                     header: 'Napomena' },
];

const DATE_FIELDS_PLC = new Set(['EntryTime']);
const DATE_FIELDS_VHL = new Set(['UseBegin', 'UseEnd']);

const ACTIVITY_LABELS_XLS = {
  'break/rest': 'Odmor/Pauza', availability: 'Raspoloživost',
  work: 'Rad', driving: 'Vožnja', unknown: 'Nepoznato', shortrest: 'Kratki odmor',
};

function minsToHHMM(min) {
  if (min == null) return '';
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

const KazneIzvestaji = ({ grupa = 'VOZACI' }) => {
  const { t } = useTranslation();
  const toast   = useRef(null);
  const auth    = useContext(AuthContext);
  const company = useSelector(state => state.company);

  const [tab,        setTab]        = useState(grupa === 'VOZILA' ? 12 : 0);
  const [dateFrom,   setDateFrom]   = useState(new Date(Date.now() - 30 * 86400000));
  const [dateTo,     setDateTo]     = useState(new Date());
  const [cards,      setCards]      = useState([]);
  const [cardsRaw,   setCardsRaw]   = useState([]);   // kartice sa podacima o poslednjem očitavanju
  const [citanjaVozaci, setCitanjaVozaci] = useState([]); // SVI vozači, i oni bez kartice
  const [selectedCard, setSelectedCard] = useState(null);
  /* Koji se propis kontroliše na dijagramu aktivnosti. SVI je podrazumevano, da
     izbor ničim ne sakrije prekršaje koji su se do sada videli. */
  const [propis, setPropis] = useState('SVI');
  const [tableData,  setTableData]  = useState([]);
  const [load,       setLoad]       = useState(false);
  const [vuDownloads, setVuDownloads] = useState([]);
  const [selectedVozilo, setSelectedVozilo] = useState(null);
  const [vuActivities, setVuActivities] = useState([]);
  const [citanjaVozaciFilter, setCitanjaVozaciFilter] = useState(null);
  const [citanjaVozilaFilter, setCitanjaVozilaFilter] = useState(null);
  const [fajlovi,       setFajlovi]       = useState([]);
  const [fajloviFilter, setFajloviFilter] = useState(null);
  /* Pretraga na oba Pregleda aktivnosti (vozači i vozila) — do sada su te dve
     tabele jedine u modulu bile bez nje. */
  const [aktivnostiFilter, setAktivnostiFilter] = useState(null);
  const [vuAktivnostiFilter, setVuAktivnostiFilter] = useState(null);
  const searchedRef = useRef(false);   // da li je korisnik bar jednom pokrenuo pretragu

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/cards', {
      headers: { Authorization: 'Bearer ' + auth.token },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCardsRaw(data);   // pun set (za tab "Poslednja čitanja")
          setCards(data.map(c => ({
            label: c.HolderName || c.CardNumber,
            value: c.EmployeeId,
          })));
        }
      })
      .catch(() => {});
  }, [auth.token]);

  /* Poslednja očitavanja kartica: lista mora sadržati SVE aktivne vozače,
     i one kojima taho kartica nije upisana (red ostaje beo, status to kaže). */
  useEffect(() => {
    if (grupa !== 'VOZACI') return;
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/cardDownloads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setCitanjaVozaci(Array.isArray(d) ? d : []))
      .catch(() => setCitanjaVozaci([]));
  }, [grupa, auth.token]);

  // Poslednja očitavanja jedinica vozila (rok 90 dana) — povuci kad se otvori grupa VOZILA
  useEffect(() => {
    if (grupa !== 'VOZILA') return;
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/vehicleDownloads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setVuDownloads(Array.isArray(d) ? d : []))
      .catch(() => setVuDownloads([]));
  }, [grupa, auth.token]);

  /* Redosled u "Poslednja čitanja" ide po hitnosti:
       1. očitani — najstarije očitavanje prvo (najviše dana od čitanja)
       2. sa karticom, ali nikad očitani
       3. bez upisane kartice (beli redovi) — na kraju */
  const citanjaVozaciSort = useMemo(() => {
    const grupa = (r) => (r.NemaKarticu ? 2 : (r.LastDownload == null ? 1 : 0));
    return [...(citanjaVozaci || [])].sort((a, b) => {
      const ga = grupa(a), gb = grupa(b);
      if (ga !== gb) return ga - gb;
      if (ga === 0) return (b.DanaOdOcitavanja ?? 0) - (a.DanaOdOcitavanja ?? 0);
      return String(a.HolderName || '').localeCompare(String(b.HolderName || ''), 'sr');
    });
  }, [citanjaVozaci]);

  const vozilaOpcije = (vuDownloads || []).map(v => ({ label: v.Registracija, value: v.VoziloId }));

  /* Tab "Aktivnosti vozila" koristi isti izvor i iste filtere kao dijagram. */
  useEffect(() => {
    if (tab !== 10 || !selectedVozilo) { setVuActivities([]); return; }
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/vehicleActivities', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ ...opsegZaUpit(toISO(dateFrom), toISO(dateTo)), voziloId: selectedVozilo }),
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setVuActivities(Array.isArray(d) ? d : []))
      .catch(() => setVuActivities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedVozilo, dateFrom, dateTo, auth.token]);

  /* Sačuvani originalni DDD fajlovi (tab "Fajlovi čitanja"). Izdvojeno iz efekta
     da bi se spisak mogao osvežiti i posle brisanja jednog očitavanja. */
  const ucitajFajlove = useCallback((zaTab) => {
    const koji = zaTab ?? tab;
    if (koji !== 7 && koji !== 13) return;
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/files', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ tip: koji === 13 ? 'VOZILO' : 'VOZAC' }),
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setFajlovi(Array.isArray(d) ? d : []))
      .catch(() => setFajlovi([]));
  }, [tab, auth.token]);

  useEffect(() => { ucitajFajlove(tab); }, [tab, ucitajFajlove]);

  /* Brisanje jednog očitavanja: uvek odlazi original sa servera i njegova evidencija.
     Ako je to bio POSLEDNJI fajl tog vozača/vozila, backend briše i pročitane podatke,
     pa ga više nema ni u pregledu aktivnosti ni u padajućem spisku — zato se tada i
     pita drugačije. */
  const obrisiFajl = (red) => {
    const poslednji = (fajlovi || []).filter(
      f => f.Tip === red.Tip && String(f.EntityId) === String(red.EntityId)
    ).length <= 1;
    confirmDialog({
      message: poslednji
        ? t('taho.brisanjePoslednjegPitanje', { naziv: red.Naziv || red.FileName })
        : t('taho.brisanjeFajlaPitanje', { naziv: red.FileName }),
      header: t('taho.brisanjeOcitavanja'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
      acceptClassName: 'p-button-danger',
      accept: () => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/deleteFile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
          body: JSON.stringify({ id: red.Id }),
        })
          .then(async (r) => {
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || t('taho.brisanjeGreska'));
            return d;
          })
          .then((d) => {
            toast.current?.show({
              severity: 'success',
              summary: d.obrisanoOcitavanje ? t('taho.ocitavanjeObrisano') : t('taho.fajlObrisan'),
              life: 4000,
            });
            ucitajFajlove();
          })
          .catch((e) => {
            toast.current?.show({ severity: 'error', summary: e.message || t('taho.brisanjeGreska'), life: 4000 });
          });
      },
    });
  };

  const toISO = d => d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    : (d || '');

  const handleSearch = async () => {
    if (!dateFrom || !dateTo) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Unesite opseg datuma', life: 3000 });
      return;
    }
    /* Tabovi vozila (10/11) sami povlače podatke kroz svoj efekat čim se promeni
       vozilo ili period — ovde nema šta da se traži. */
    if (tab >= 10) { searchedRef.current = true; return; }

    setLoad(true);
    setTableData([]);
    searchedRef.current = true;

    const endpoint = (tab === 0 || tab === 4) ? '/tacho/activities'
                   : tab === 1 ? '/tacho/places'
                   :              '/tacho/vehicles';
    try {
      /* Od baze se traži dan više sa svake strane — lokalni dan zahvata dva UTC
         dana, pa bez toga fali prva 2h prvog i poslednja 2h zadnjeg dana.
         Tabele se posle skraćuju na traženi opseg (`prikaz`). */
      const body = opsegZaUpit(toISO(dateFrom), toISO(dateTo));
      if (selectedCard) body.employeeId = selectedCard;

      const r = await fetch(process.env.REACT_APP_BACKEND_URL + endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth.token,
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) {
        setTableData(Array.isArray(data) ? data : []);
      } else {
        toast.current.show({ severity: 'error', summary: 'Greška', detail: data.message || 'Greška pri učitavanju', life: 4000 });
      }
    } catch {
      toast.current.show({ severity: 'error', summary: 'Greška', detail: 'Greška pri komunikaciji sa serverom', life: 4000 });
    }
    setLoad(false);
  };

  const handleTabChange = (index) => {
    setTab(index);
    setTableData([]);
  };

  /* Zadrži izabrane filtere: kad se pređe na drugi tab, a pretraga je već jednom
     pokrenuta, automatski povuci podatke za taj tab (bez ponovnog klika na "Prikaži"). */
  useEffect(() => {
    // tab 3 (KAZNE AETR) sam povlači svoje podatke iz istog filtera
    if (searchedRef.current && !TABS_BEZ_FILTERA.includes(tab) && tab !== 3 && tab < 10) handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* `tableData` i `vuActivities` su SIROVI podaci iz baze — UTC, i jedan dan širi
     sa svake strane (`opsegZaUpit`). Tabele i Excel rade nad `prikaz`/`vuPrikaz`:
     prebaceno u lokalnu zonu i skraćeno na opseg koji je korisnik tražio.
     Dijagram (tab 4) namerno dobija SIROVE podatke — sam seče segmente preko
     ponoći i sam odbacuje dane van opsega. */
  const prikaz = useMemo(() => {
    const od = toISO(dateFrom), doo = toISO(dateTo);
    const red = Array.isArray(tableData) ? tableData : [];
    const dan = v => String(v || '').substring(0, 10);
    if (tab === 0) {
      return segmentiULokalno(red).filter(r => uOpsegu(dan(r.ActivityDate), od, doo));
    }
    if (tab === 1) {
      return red.map(r => poljeULokalno(r, ['EntryTime']))
                .filter(r => uOpsegu(dan(r.EntryTime), od, doo));
    }
    if (tab === 2) {
      /* korišćenje vozila ume da pređe ponoć — red ostaje ako se PRESECA sa opsegom */
      return red.map(r => poljeULokalno(r, ['UseBegin', 'UseEnd']))
                .filter(r => uOpsegu(dan(r.UseEnd || r.UseBegin), od, null)
                          && uOpsegu(dan(r.UseBegin || r.UseEnd), null, doo));
    }
    return red;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tab, dateFrom, dateTo]);

  const vuPrikaz = useMemo(() => {
    const od = toISO(dateFrom), doo = toISO(dateTo);
    return segmentiULokalno(Array.isArray(vuActivities) ? vuActivities : [])
      .filter(r => uOpsegu(String(r.ActivityDate || '').substring(0, 10), od, doo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vuActivities, dateFrom, dateTo]);

  /* ZASTO JE PRAZNO — "Nema podataka" nije odgovor.
     Aktivnosti postoje samo za dane koji su razlozeni iz DDD ocitavanja jedinice.
     Firma koja jedinice ocitava redovno ima i tekuci mesec; firma koja nije
     ocitavala od avgusta nema ni jedan dan posle avgusta — i tabela je prazna
     sasvim ispravno. Dok je stajalo samo "Nema podataka", to se nije moglo
     razlikovati od kvara (task 144: "ne vidim aktivnosti, a na Sarcevicu vidim").

     Zato se na prazan rezultat kaze STA se ima: nijedno ocitavanje, ili
     ocitavanje van izabranog perioda — uz datume koji to pokazuju. */
  const vuPrazno = useMemo(() => {
    if (!selectedVozilo) return t('taho.izaberiJednoVozilo');
    const v = (vuDownloads || []).find(x => x.VoziloId === selectedVozilo);
    if (!v || (!v.DanaAktivnosti && !v.LastDownload)) return t('taho.nemaOcitavanjaVozila');
    /* Fajl je ucitan, a iz njega nije izaslo nijedno dnevno zaglavlje — to NIJE
       isto sto i "niko nije ocitao" i ne sme se tako prikazati. Zateceno na
       pravim podacima: ZG5631KL ima ocitavanje od 16.07.2026, a nula razlozenih
       dana. Operater po ovoj poruci zna da ponovi ocitavanje ili da prijavi fajl. */
    if (!v.DanaAktivnosti) return t('taho.ocitavanjeBezDana', { kad: formatDT(v.LastDownload) });
    return t('taho.aktivnostiVanPerioda', {
      od: formatDT(v.PrviDanAktivnosti),
      do: formatDT(v.PoslednjiDanAktivnosti),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVozilo, vuDownloads, t]);

  const activityTypeBody = (row) => ACTIVITY_LABELS[row.ActivityType] || row.ActivityType || '—';
  const startTimeBody    = (row) => minutesToHHMM(row.StartMinute);
  const durationBody     = (row) => minutesToHHMM(row.Duration);
  const entryTimeBody    = (row) => formatDT(row.EntryTime);

  const exportExcelPlaces = async () => {
    if (!prikaz || prikaz.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Lokacije');
    const headers   = PLC_COLS.map(c => c.header);
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    prikaz.forEach(rowData => {
      const row = worksheet.addRow(PLC_COLS.map(c => {
        const v = rowData[c.field];
        return DATE_FIELDS_PLC.has(c.field) ? formatDT(v) : (v ?? '');
      }));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const field   = PLC_COLS[i]?.field;
      const lengths = [headers[i].length, ...prikaz.map(r => String(r[field] ?? '').length)];
      col.width = Math.max(...lengths) + 2;
    });
    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Lokacije - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`);
  };

  /* Zajednički izvoz za tabove "Poslednja čitanja" (kartice vozača i jedinice vozila). */
  const exportCitanja = async (rows, kolone, naziv) => {
    if (!rows || rows.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(naziv.substring(0, 30));
    const headers   = kolone.map(c => c.header);
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    rows.forEach(r => {
      const row = worksheet.addRow(kolone.map(c => (c.value ? c.value(r) : (r[c.field] ?? ''))));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const lengths = [headers[i].length,
        ...rows.map(r => String(kolone[i].value ? kolone[i].value(r) : (r[kolone[i].field] ?? '')).length)];
      col.width = Math.max(...lengths) + 2;
    });
    const dt = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `${naziv} - ${company?.companyName ?? ''} - ${dt}.xlsx`);
  };

  /* Kolona "Fajl": ako je originalni DDD sačuvan na serveru — link (isti izgled
     kao u Administracija → Zaposleni → Pregled), inače samo naziv iz očitavanja. */
  const fajlBody = (r) => {
    const url = getDocumentLocation(r.SavedFilePath);
    if (!url) return r.LastDownloadFile || '—';
    const naziv = String(r.SavedFileName || r.LastDownloadFile || '').replace(/\.ddd$/i, '');
    return (
      <Button
        label={naziv}
        icon="pi pi-eye"
        className="p-button-text p-button-sm kazne-fajl-link"
        onClick={() => window.open(url, '_blank')}
        tooltip={t('taho.pregledajDokument')}
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  const statusCitanja = (r, rok) => {
    if (r.NemaKarticu) return t('taho.nemaBrojKartice');
    if (r.LastDownload == null) return t('taho.nemaOcitavanja');
    if (r.OcitavanjeIsteklo) return `${t('taho.rokIstekao')} (${r.DanaOdOcitavanja - rok})`;
    return `${t('taho.uRedu')} (${r.DanaDoRoka})`;
  };

  const headerCitanja = (onExport, filterSetter, klasa) => (
    <div className="export-buttons fakture-stavke-header">
      <Tooltip target={`.${klasa}`} position="bottom" />
      <Button type="button" icon="pi pi-file-excel" severity="success" rounded
        onClick={onExport} className={klasa} data-pr-tooltip="Export XLS" />
      <input type="search" placeholder={t('common.pretraga')} className="p-inputtext p-component"
        onInput={e => filterSetter(e.target.value)} />
    </div>
  );

  const exportExcelVehicles = async () => {
    if (!prikaz || prikaz.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Vozila');
    const headers   = VHL_COLS.map(c => c.header);
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    prikaz.forEach(rowData => {
      const row = worksheet.addRow(VHL_COLS.map(c => {
        const v = rowData[c.field];
        if (c.field === 'Tudje') return v ? 'TUĐE' : '';
        return DATE_FIELDS_VHL.has(c.field) ? formatDT(v) : (v ?? '');
      }));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });
    worksheet.columns.forEach((col, i) => {
      const field   = VHL_COLS[i]?.field;
      const lengths = [headers[i].length, ...prikaz.map(r => String(r[field] ?? '').length)];
      col.width = Math.max(...lengths) + 2;
    });
    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Vozila - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`);
  };

  const exportExcelActivities = async () => {
    if (!prikaz || prikaz.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Aktivnosti');
    const headers   = ACT_COLS.map(c => c.header);

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });

    prikaz.forEach(rowData => {
      const row = worksheet.addRow(ACT_COLS.map(c => {
        const v = rowData[c.field];
        if (c.field === 'ActivityDate')  return String(v || '').substring(0, 10).split('-').reverse().join('.');
        if (c.field === 'ActivityType')  return ACTIVITY_LABELS_XLS[v] || v || '';
        if (c.field === 'StartMinute')   return minsToHHMM(v);
        if (c.field === 'Duration')      return minsToHHMM(v);
        return v ?? '';
      }));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });

    worksheet.columns.forEach((col, i) => {
      const field   = ACT_COLS[i]?.field;
      const lengths = [headers[i].length, ...prikaz.map(r => String(r[field] ?? '').length)];
      col.width = Math.max(...lengths) + 2;
    });

    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Aktivnosti - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`
    );
  };

  /* Izvoz aktivnosti OČITANIH IZ VOZILA (tab 10). Ne deli se sa izvozom
     aktivnosti sa kartice vozača: kolone nisu iste — ovde su registracija,
     vozač sa ulogom (vozač/suvozač), broj kartice kojom je bio prijavljen i
     stanje brojčanika, čega na kartici nema. */
  const exportExcelVuActivities = async () => {
    if (!vuPrikaz || vuPrikaz.length === 0) return;

    const KOLONE = [
      { header: t('ture.registracija'), vrednost: r => r.Registracija ?? '' },
      { header: t('taho.datum'),        vrednost: r => formatDT(r.ActivityDate) },
      { header: t('taho.vozac'),        vrednost: r => {
          const uloga = r.Slot === 1 ? t('taho.suvozac') : t('taho.vozac');
          return r.VozacIme ? `${r.VozacIme} (${uloga})` : uloga;
        } },
      { header: t('taho.brojKartice'),  vrednost: r => r.VozacKartica ?? '' },
      { header: t('mk.pocetak'),        vrednost: r => minutesToHHMM(r.StartMinute) },
      { header: t('mk.trajanje'),       vrednost: r => minutesToHHMM(r.Duration) },
      { header: t('taho.aktivnost'),    vrednost: r => ACTIVITY_LABELS[r.ActivityType] || r.ActivityType || '' },
      { header: t('taho.odometarKraj'), vrednost: r => (r.OdometerKraj == null ? '' : `${r.OdometerKraj} km`) },
    ];

    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Aktivnosti vozila');

    const headerRow = worksheet.addRow(KOLONE.map(k => k.header));
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });

    vuPrikaz.forEach(r => {
      const row = worksheet.addRow(KOLONE.map(k => k.vrednost(r)));
      row.eachCell(cell => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });

    worksheet.columns.forEach((col, i) => {
      const duzine = [KOLONE[i].header.length, ...vuPrikaz.map(r => String(KOLONE[i].vrednost(r) ?? '').length)];
      col.width = Math.max(...duzine) + 2;
    });

    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Aktivnosti vozila - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`
    );
  };

  const headerPlaces = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Tooltip target=".izv-plc-xls" position="bottom" />
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded
          onClick={exportExcelPlaces} className="izv-plc-xls" data-pr-tooltip="Export XLS" />
      </div>
    </div>
  );

  const headerVehicles = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Tooltip target=".izv-vhl-xls" position="bottom" />
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded
          onClick={exportExcelVehicles} className="izv-vhl-xls" data-pr-tooltip="Export XLS" />
      </div>
    </div>
  );

  return (
    <div id="zaposleni_main">
      {load && <Loading />}
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Tabovi i filteri ostaju vidljivi dok sadrzaj ispod njih skroluje */}
      <div className="kazne-zaglavlje">
      <div className="kazne-wh-tabs" style={{ marginTop: 0 }}>
        {(grupa === 'VOZACI' ? TABS_VOZACI : TABS_VOZILA).map(tb => (
          <button
            key={tb.index}
            className={'kazne-wh-tab' + (tab === tb.index ? ' kazne-wh-tab--active' : '')}
            onClick={() => handleTabChange(tb.index)}
          >
            {t(tb.kljuc).toUpperCase()}
          </button>
        ))}
      </div>

      {/* Zajednički filteri stoje ISPOD tabova i važe za tabove koji ih koriste. */}
      {!TABS_BEZ_FILTERA.includes(tab) && (
        <div className="kazne-izv-filters">
          <div className="kazne-izv-filter-row">
            <label className="kazne-izv-label">{t('mk.odDatuma')}</label>
            <Calendar
              value={dateFrom}
              onChange={e => setDateFrom(e.value)}
              showIcon
              dateFormat="dd.mm.yy"
              mask="99.99.9999"
            />
            <label className="kazne-izv-label">{t('mk.doDatuma')}</label>
            <Calendar
              value={dateTo}
              onChange={e => setDateTo(e.value)}
              showIcon
              dateFormat="dd.mm.yy"
              mask="99.99.9999"
            />
            {/* Dijagram vozila radi za JEDNO vozilo — zato tu ide izbor vozila,
                a ne vozača (i bira se samo jedno). */}
            {(tab === 10 || tab === 11) ? (
              <>
                <label className="kazne-izv-label">{t('nova.vozilo')}</label>
                <CustomDropdown
                  value={selectedVozilo}
                  options={vozilaOpcije}
                  onChange={e => setSelectedVozilo(e.target.value)}
                  optionLabel="label"
                  placeholder={t('taho.izaberiVozilo')}
                  className="kazne-izv-dropdown"
                />
              </>
            ) : (
              <>
                <label className="kazne-izv-label">{t('ture.vozac')}</label>
                <CustomDropdown
                  value={selectedCard}
                  options={cards}
                  onChange={e => setSelectedCard(e.target.value)}
                  optionLabel="label"
                  placeholder={t('ture.sviVozaci')}
                  className="kazne-izv-dropdown"
                />
              </>
            )}
            {/* Propis ima smisla samo tamo gde se prekršaji računaju — na dijagramu
                aktivnosti. Ostali tabovi prikazuju sirove podatke. */}
            {tab === 4 && (
              <>
                <label className="kazne-izv-label">{t('mk.propis')}</label>
                <CustomDropdown
                  value={propis}
                  /* Interni propisi Srbije se ne nude firmi van Srbije — vidi
                     polje `drzave` u propisi.js. */
                  options={[{ label: t('mk.sviPropisi'), value: 'SVI' },
                            ...propisiZaDrzavu(company?.companyCountryID ?? null)
                                .map(x => ({ label: t(x.i18nKljuc), value: x.kljuc }))]}
                  onChange={e => setPropis(e.target.value)}
                  optionLabel="label"
                  showClear={false}
                  className="kazne-izv-dropdown"
                  style={{ minWidth: '200px' }}
                />
              </>
            )}
            <Button
              label={t('mk.prikazi')}
              icon="pi pi-search"
              onClick={handleSearch}
              className="p-button p-button-primary"
            />
          </div>
        </div>
      )}
      </div>

      {/* Skrol je SAMO ovde — tabovi i filteri iznad ostaju na mestu */}
      <div className="kazne-sadrzaj">
      {tab === 0 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={prikaz}
            paginator
            rows={50}
            rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')}
            size="small"
            scrollable
            scrollHeight="55vh"
            resizableColumns
            columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            globalFilter={aktivnostiFilter}
            header={headerCitanja(exportExcelActivities, setAktivnostiFilter, 'izv-act-xls')}
          >
            <Column field="HolderName"     header={t('ture.vozac')}      sortable style={{ minWidth: '150px' }} />
            <Column field="CardNumber"     header={t('magacin.kartica')}    sortable style={{ minWidth: '140px' }} />
            <Column field="ActivityDate"   header={t('nova.datum')}      sortable style={{ minWidth: '110px' }} body={r => String(r.ActivityDate || '').substring(0, 10).split('-').reverse().join('.')} />
            <Column field="ActivityType"   header={t('mk.aktivnost')}  sortable style={{ minWidth: '130px' }} body={activityTypeBody} />
            <Column field="StartMinute"    header={t('mk.pocetak')}    sortable style={{ minWidth: '100px' }} body={startTimeBody} />
            <Column field="Duration"       header={t('mk.trajanje')}   sortable style={{ minWidth: '100px' }} body={durationBody} />
          </DataTable>
        </div>
      )}

      {tab === 4 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <AktivnostiDijagram data={tableData} selectedCard={selectedCard} dateFrom={dateFrom} dateTo={dateTo}
                              propis={propis} idDrzava={company?.companyCountryID ?? null} />
        </div>
      )}

      {tab === 1 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={prikaz}
            paginator
            rows={50}
            rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')}
            size="small"
            scrollable
            scrollHeight="55vh"
            resizableColumns
            columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            header={headerPlaces}
          >
            <Column field="HolderName"  header={t('ture.vozac')}         sortable style={{ minWidth: '150px' }} />
            <Column field="CardNumber"  header={t('magacin.kartica')}       sortable style={{ minWidth: '140px' }} />
            <Column field="EntryTime"   header={t('mk.vremeUlaska')}  sortable style={{ minWidth: '160px' }} body={entryTimeBody} />
            <Column field="Drzava"      header={t('ture.drzava')}        sortable style={{ minWidth: '140px' }} />
            <Column field="OdometerKm"  header={t('mk.kmOdometar')} sortable style={{ minWidth: '120px' }} />
          </DataTable>
        </div>
      )}

      {tab === 2 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={prikaz}
            paginator
            rows={50}
            rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')}
            size="small"
            scrollable
            scrollHeight="55vh"
            resizableColumns
            columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            header={headerVehicles}
            rowClassName={r => r.Tudje ? 'vhl-row-tudje' : ''}
          >
            <Column field="HolderName"                header={t('ture.vozac')}          sortable style={{ minWidth: '150px' }} />
            <Column field="CardNumber"                header={t('magacin.kartica')}        sortable style={{ minWidth: '140px' }} />
            <Column field="VehicleRegistrationNumber" header={t('ture.registracija')}   sortable style={{ minWidth: '130px' }} />
            <Column field="VehicleRegistrationNation" header={t('mk.drzavaReg')}    sortable style={{ minWidth: '110px' }} />
            <Column field="UseBegin"                  header={t('mk.koriscenjeOd')}  sortable style={{ minWidth: '160px' }} body={r => formatDT(r.UseBegin)} />
            <Column field="UseEnd"                    header={t('mk.koriscenjeDo')}  sortable style={{ minWidth: '160px' }} body={r => formatDT(r.UseEnd)} />
            <Column field="OdometerBeginKm"           header={t('mk.kmPocetak')}     sortable style={{ minWidth: '110px' }} />
            <Column field="OdometerEndKm"             header={t('mk.kmKraj')}        sortable style={{ minWidth: '110px' }} />
            <Column field="Tudje" header={t('nova.napomena')} style={{ minWidth: '90px' }}
              body={r => r.Tudje ? <span className="vhl-badge-tudje">TUĐE</span> : null} />
          </DataTable>
        </div>
      )}

      {/* Preuzima zajednički filter (period + vozač) i sam prikazuje sve kazne */}
      {tab === 3 && (
        <KazneKazne spoljniFilter dateFrom={dateFrom} dateTo={dateTo} selectedCard={selectedCard} />
      )}

      {tab === 6 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          {/* Dvosmerno: preuzima zajednički period i vozača, a režimi 28/56 dana
              vraćaju stvarni opseg nazad, pa ostali tabovi hvataju iste dane. */}
          <AnalizaNadoknada
            cards={cards}
            dateFrom={dateFrom}
            dateTo={dateTo}
            selectedCard={selectedCard}
            onFilterChange={({ dateFrom: od, dateTo: doo, selectedCard: vozac }) => {
              if (od) setDateFrom(od);
              if (doo) setDateTo(doo);
              if (vozac != null) setSelectedCard(vozac);
              searchedRef.current = true;
            }}
          />
        </div>
      )}

      {/* ── IZVEŠTAJI VOZILA ── */}
      {tab === 11 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <VozilaDijagram dateFrom={dateFrom} dateTo={dateTo} voziloId={selectedVozilo} />
        </div>
      )}

      {tab === 10 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={vuPrikaz}
            paginator rows={50} rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={vuPrazno}
            size="small" scrollable scrollHeight="55vh"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            globalFilter={vuAktivnostiFilter}
            header={headerCitanja(exportExcelVuActivities, setVuAktivnostiFilter, 'izv-vu-act-xls')}
          >
            <Column field="Registracija" header={t('ture.registracija')} sortable style={{ minWidth: '120px' }} />
            <Column field="ActivityDate" header={t('taho.datum')} sortable style={{ minWidth: '120px' }}
              body={(r) => formatDT(r.ActivityDate)} />
            <Column field="VozacIme" header={t('taho.vozac')} sortable style={{ minWidth: '210px' }}
              body={(r) => {
                const uloga = r.Slot === 1 ? t('taho.suvozac') : t('taho.vozac');
                return r.VozacIme ? `${r.VozacIme} (${uloga})` : uloga;
              }} />
            {/* Broj kartice kojom je vozač bio prijavljen — iz DDD očitavanja vozila.
                Monospace jer se ovi brojevi porede znak po znak. */}
            <Column field="VozacKartica" header={t('taho.brojKartice')} sortable style={{ minWidth: '180px' }}
              body={(r) => r.VozacKartica
                ? <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{r.VozacKartica}</span>
                : <span style={{ color: '#8696a0' }}>—</span>} />
            <Column field="StartMinute" header={t('mk.pocetak')} sortable style={{ minWidth: '100px' }}
              body={(r) => minutesToHHMM(r.StartMinute)} />
            <Column field="Duration" header={t('mk.trajanje')} sortable style={{ minWidth: '100px' }}
              body={(r) => minutesToHHMM(r.Duration)} />
            <Column field="ActivityType" header={t('taho.aktivnost')} sortable style={{ minWidth: '140px' }}
              body={(r) => ACTIVITY_LABELS[r.ActivityType] || r.ActivityType || '—'} />
            <Column field="OdometerKraj" header={t('taho.odometarKraj')} sortable style={{ minWidth: '140px' }}
              body={(r) => (r.OdometerKraj == null ? '—' : `${r.OdometerKraj} km`)} />
          </DataTable>
        </div>
      )}

      {tab === 12 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={vuDownloads}
            paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')} size="small" scrollable scrollHeight="55vh"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            sortField="DanaOdOcitavanja" sortOrder={-1}
            globalFilter={citanjaVozilaFilter}
            header={headerCitanja(
              () => exportCitanja(vuDownloads, [
                { header: 'Registracija', field: 'Registracija' },
                { header: 'Broj šasije (VIN)', field: 'VIN' },
                { header: 'Poslednje očitavanje', value: r => formatDT(r.LastDownload) },
                { header: 'Dana od očitavanja', value: r => (r.DanaOdOcitavanja ?? '') },
                { header: 'Rok (dana)', value: () => ROK_VOZILO_DANA },
                { header: 'Status', value: r => statusCitanja(r, ROK_VOZILO_DANA) },
                { header: 'Fajl', field: 'LastDownloadFile' },
              ], 'Poslednja citanja vozila'),
              setCitanjaVozilaFilter, 'izv-cit-vzl-xls')}
            rowClassName={(row) => (row.OcitavanjeIsteklo ? 'row-invoice-overdue' : '')}
          >
            <Column field="Registracija" header={t('ture.registracija')} sortable style={{ minWidth: '140px' }} />
            <Column field="VIN" header={t('taho.brojSasije')} sortable style={{ minWidth: '180px' }}
              body={(r) => r.VIN || '—'} />
            <Column field="LastDownload" header={t('taho.poslednjeOcitavanje')} sortable style={{ minWidth: '150px' }}
              body={(r) => formatDT(r.LastDownload)} />
            <Column field="DanaOdOcitavanja" header={t('taho.danaOdOcitavanja')} sortable style={{ minWidth: '120px' }}
              body={(r) => (r.DanaOdOcitavanja == null ? '—' : r.DanaOdOcitavanja)} />
            <Column header={t('taho.rokDana')} style={{ minWidth: '90px' }} body={() => ROK_VOZILO_DANA} />
            <Column header={t('taho.status')} sortable sortField="OcitavanjeIsteklo" style={{ minWidth: '150px' }}
              body={(r) => (
                r.LastDownload == null
                  ? <span style={{ color: '#d32f2f', fontWeight: 700 }}>{t('taho.nemaOcitavanja')}</span>
                  : r.OcitavanjeIsteklo
                    ? <span style={{ color: '#d32f2f', fontWeight: 700 }}>{t('taho.rokIstekao')} ({r.DanaOdOcitavanja - ROK_VOZILO_DANA})</span>
                    : <span style={{ color: '#2e7d32', fontWeight: 600 }}>{t('taho.uRedu')} ({r.DanaDoRoka})</span>
              )} />
            <Column field="LastDownloadFile" header={t('taho.fajl')} style={{ minWidth: '240px' }} body={fajlBody} />
          </DataTable>
        </div>
      )}

      {tab === 5 && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={citanjaVozaciSort}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')}
            size="small"
            scrollable
            scrollHeight="55vh"
            resizableColumns
            columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            globalFilter={citanjaVozaciFilter}
            header={headerCitanja(
              () => exportCitanja(citanjaVozaciSort, [
                { header: 'Vozač', field: 'HolderName' },
                { header: 'Kartica', field: 'CardNumber' },
                { header: 'Poslednje očitavanje', value: r => formatDT(r.LastDownload) },
                { header: 'Dana od očitavanja', value: r => (r.DanaOdOcitavanja ?? '') },
                { header: 'Rok (dana)', value: () => ROK_KARTICA_DANA },
                { header: 'Status', value: r => statusCitanja(r, ROK_KARTICA_DANA) },
                { header: 'Fajl', field: 'LastDownloadFile' },
              ], 'Poslednja citanja kartica vozaca'),
              setCitanjaVozaciFilter, 'izv-cit-voz-xls')}
            /* vozač bez upisane kartice nije u prekoračenju — red ostaje beo */
            rowClassName={(row) => (row.OcitavanjeIsteklo && !row.NemaKarticu ? 'row-invoice-overdue' : '')}
          >
            <Column field="HolderName" header={t('ture.vozac')} sortable style={{ minWidth: '180px' }} />
            <Column field="CardNumber" header={t('mk.kartica')} sortable style={{ minWidth: '170px' }}
              body={(r) => r.CardNumber || '—'} />
            <Column field="LastDownload" header={t('taho.poslednjeOcitavanje')} sortable style={{ minWidth: '150px' }}
              body={(r) => formatDT(r.LastDownload)} />
            <Column field="DanaOdOcitavanja" header={t('taho.danaOdOcitavanja')} sortable style={{ minWidth: '120px' }}
              body={(r) => (r.DanaOdOcitavanja == null ? '—' : r.DanaOdOcitavanja)} />
            <Column header={t('taho.rokDana')} style={{ minWidth: '100px' }} body={() => ROK_KARTICA_DANA} />
            <Column header={t('taho.status')} sortable sortField="OcitavanjeIsteklo" style={{ minWidth: '150px' }}
              body={(r) => (
                r.NemaKarticu
                  ? <span style={{ color: '#6b7280', fontWeight: 600 }}>{t('taho.nemaBrojKartice')}</span>
                  : r.LastDownload == null
                    ? <span style={{ color: '#d32f2f', fontWeight: 700 }}>{t('taho.nemaOcitavanja')}</span>
                    : r.OcitavanjeIsteklo
                      ? <span style={{ color: '#d32f2f', fontWeight: 700 }}>{t('taho.rokIstekao')} ({r.DanaOdOcitavanja - ROK_KARTICA_DANA})</span>
                      : <span style={{ color: '#2e7d32', fontWeight: 600 }}>{t('taho.uRedu')} ({r.DanaDoRoka})</span>
              )} />
            <Column field="LastDownloadFile" header={t('taho.fajl')} style={{ minWidth: '260px' }} body={fajlBody} />
          </DataTable>
        </div>
      )}

      {/* Sačuvani originalni DDD fajlovi — isti prikaz za vozače (7) i vozila (13). */}
      {(tab === 7 || tab === 13) && (
        <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
          <DataTable
            value={fajlovi}
            paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
            emptyMessage={t('common.noData')} size="small" scrollable scrollHeight="55vh"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            sortField="UploadedAt" sortOrder={-1}
            globalFilter={fajloviFilter}
            header={headerCitanja(
              () => exportCitanja(fajlovi, [
                { header: tab === 13 ? 'Vozilo' : 'Vozač', field: 'Naziv' },
                { header: 'Datum učitavanja', value: r => formatDT(r.UploadedAt) },
                { header: 'Poslednja aktivnost', value: r => formatDT(r.PoslednjaAktivnost) },
                { header: 'Naziv fajla', field: 'FileName' },
              ], tab === 13 ? 'Fajlovi citanja vozila' : 'Fajlovi citanja vozaca'),
              setFajloviFilter, 'izv-fajl-xls')}
          >
            <Column field="Naziv" header={tab === 13 ? t('nova.vozilo') : t('ture.vozac')} sortable
              style={{ minWidth: '200px' }} body={(r) => r.Naziv || '—'} />
            <Column field="UploadedAt" header={t('taho.datumUcitavanja')} sortable style={{ minWidth: '150px' }}
              body={(r) => formatDT(r.UploadedAt)} />
            <Column field="PoslednjaAktivnost" header={t('taho.poslednjaAktivnost')} sortable style={{ minWidth: '160px' }}
              body={(r) => formatDT(r.PoslednjaAktivnost)} />
            {/* Isti izgled linka kao u Administracija → Zaposleni → Pregled */}
            <Column field="FileName" header={t('taho.fajl')} sortable style={{ minWidth: '320px' }}
              body={(r) => {
                const url = getDocumentLocation(r.FilePath);
                if (!url) return r.FileName || '—';
                const naziv = String(r.FileName || '').replace(/\.ddd$/i, '');
                return (
                  <Button
                    label={naziv}
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm"
                    onClick={() => window.open(url, '_blank')}
                    tooltip={t('taho.pregledajDokument')}
                    tooltipOptions={{ position: 'top' }}
                  />
                );
              }} />
            {/* Brisanje očitavanja — original sa servera i njegova evidencija. Ako je to
                poslednji fajl tog vozača/vozila, odlaze i pročitani podaci (potvrda to kaže). */}
            <Column header={t('common.delete')} style={{ minWidth: '90px' }}
              body={(r) => (
                <Button type="button" icon="pi pi-trash" severity="danger" rounded text
                  onClick={() => obrisiFajl(r)}
                  tooltip={t('taho.obrisiOcitavanje')}
                  tooltipOptions={{ position: 'top' }} />
              )} />
          </DataTable>
        </div>
      )}
      </div>
    </div>
  );
};

export default KazneIzvestaji;
